import { useCallback } from 'react'

import { ExtraPropertyEntityType } from '@tribeplatform/gql-client/types'
import { useTracker } from '@tribeplatform/react-sdk'
import {
  useAuthMember,
  useUpsertExtraProperty,
} from '@tribeplatform/react-sdk/hooks'

export const ADMIN_MODE_ONBOARDING_ID = 'admin-mode-onboarding'
const ADMIN_MODE_ONBOARDING_EXTRA_PROPERTY_KEY = 'admin-mode-onboarding'
const GUIDES_EXTRA_PROPERTY_KEY = 'guides'
const STEP_PROGRESS_KEY_PREFIX = 'admin-mode-onboarding-step'
const COMPLETED_KEY_PREFIX = 'admin-mode-onboarding-completed'

export const useAdminModeOnboarding = () => {
  const { data: member } = useAuthMember()
  const { mutateAsync: upsertExtraProperty } = useUpsertExtraProperty()
  const { tracker } = useTracker()

  const stepProgressKey = member?.id
    ? `${STEP_PROGRESS_KEY_PREFIX}:${member.id}`
    : null

  const completedKey = member?.id
    ? `${COMPLETED_KEY_PREFIX}:${member.id}`
    : null

  const getStepProgress = useCallback((): number => {
    if (!stepProgressKey) {
      return 0
    }
    try {
      const saved = localStorage.getItem(stepProgressKey)
      return saved ? parseInt(saved, 10) : 0
    } catch {
      return 0
    }
  }, [stepProgressKey])

  const saveStepProgress = useCallback(
    (step: number) => {
      if (!stepProgressKey) {
        return
      }
      try {
        localStorage.setItem(stepProgressKey, String(step))
      } catch {
        // ignore storage errors
      }
    },
    [stepProgressKey],
  )

  const clearStepProgress = useCallback(() => {
    if (!stepProgressKey) {
      return
    }
    try {
      localStorage.removeItem(stepProgressKey)
    } catch {
      // ignore storage errors
    }
  }, [stepProgressKey])

  const getCompletedTours = useCallback((): string[] => {
    try {
      return JSON.parse(
        member?.extraProperties?.find(
          property => property.key === GUIDES_EXTRA_PROPERTY_KEY,
        )?.value || '[]',
      )
    } catch {
      return []
    }
  }, [member?.extraProperties])

  const isTourCompletedLocally = (): boolean => {
    if (!completedKey) {
      return false
    }
    try {
      return localStorage.getItem(completedKey) === '1'
    } catch {
      return false
    }
  }

  const isTourCompleted = (
    tourId: string = ADMIN_MODE_ONBOARDING_ID,
  ): boolean => {
    if (isTourCompletedLocally()) {
      return true
    }
    const completedTours = getCompletedTours()
    return completedTours.includes(tourId)
  }

  const completeTour = useCallback(
    async (tourId: string = ADMIN_MODE_ONBOARDING_ID) => {
      // Mark complete in localStorage synchronously so a page refresh
      // before the API call finishes won't re-trigger the tour.
      if (completedKey) {
        try {
          localStorage.setItem(completedKey, '1')
        } catch {
          // ignore storage errors
        }
      }
      clearStepProgress()

      const completedTours = getCompletedTours()
      if (completedTours.includes(tourId)) {
        return
      }
      completedTours.push(tourId)

      await upsertExtraProperty({
        input: {
          entityType: ExtraPropertyEntityType.Member,
          entityId: member?.id,
          key: ADMIN_MODE_ONBOARDING_EXTRA_PROPERTY_KEY,
          value: JSON.stringify(completedTours),
        },
      })

      tracker.track('Complete Admin Mode Onboarding', {
        tourId,
      })
    },
    [
      clearStepProgress,
      completedKey,
      getCompletedTours,
      member?.id,
      tracker,
      upsertExtraProperty,
    ],
  )

  return {
    hasCompletedTour: !!member && isTourCompleted(),
    completeTour,
    getStepProgress,
    saveStepProgress,
  }
}
